import { faRefresh } from "@fortawesome/pro-light-svg-icons";
import {
    faAngleDown,
    faAngleUp,
    faCircle,
    faPlus,
    faSparkles,
    faWarning,
    faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "@tanstack/react-store";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Search, VaporIcon } from "@vapor/react-icons";
import {
    Button,
    Chip,
    IconButton,
    InputAdornment,
    ListItem,
    Stack,
    TextField,
    Tooltip,
    useTheme,
} from "@vapor/react-material";
import Autocomplete from "@vapor/react-material/Autocomplete";
import {
    type ChangeEvent,
    type ReactElement,
    type SyntheticEvent,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDebouncedCallback } from "use-debounce";
import { FulfilmentStatus } from "../../../../../core/api/use-get-fulfilment-detail/FulfilmentStatus";
import type { TaxPayerDto } from "../../../../../core/api/use-get-fulfilment-tax-payers/TaxPayerDto";
import { useGetFulfilments } from "../../../../../core/api/use-get-fulfilments/useGetFulfilments";
import { useGetKPIs } from "../../../../../core/api/use-get-kpis/useGetKPIs";
import { Can } from "../../../../../core/permissions/Can";
import { fulfilmentSearchFiltersStoreSlice } from "../../../../../store/fulfilmentSearchFiltersStoreSlice";
import { store } from "../../../../../store/store";
import { FulfilmentListHeaderKPIs } from "../fulfilment-list-header-kpis/FulfilmentListHeaderKPIs";
import { TaxTinderDialog } from "../tax-tinder-dialog/TaxTinderDialog";

const REFRESHABLE_QUERY_KEYS = [
    "useGetKPIs",
    "useGetFulfilments",
    "useGetLinkableFulfilments",
] as const;

interface FulfilmentListHeaderProps {
    onClickAddCustomFulfilment: () => unknown;
}

export const FulfilmentListHeader = ({
    onClickAddCustomFulfilment,
}: FulfilmentListHeaderProps): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const fulfilmentFilterSearch = useSelector(
        store,
        (state) => state.fulfilmentSearchFilters,
    );
    const { data: fulfilments } = useGetFulfilments();
    const taxPayers = useMemo(
        () => fulfilments.taxPayers,
        [fulfilments.taxPayers],
    );

    const fullTextSearchInputRef = useRef<HTMLInputElement>(null);

    const { data: kpiData } = useGetKPIs();

    const queryClient = useQueryClient();
    const isRefreshing =
        useIsFetching({
            predicate: (query) =>
                REFRESHABLE_QUERY_KEYS.some((key) => key === query.queryKey[0]),
        }) > 0;

    const refreshData = useCallback(() => {
        REFRESHABLE_QUERY_KEYS.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key] });
        });
    }, [queryClient]);

    const [openFilters, setOpenFilters] = useState(
        fulfilmentFilterSearch.taxPayerWorkspaceId !== undefined,
    );
    const [isTaxTinderDialogOpen, setIsTaxTinderDialogOpen] = useState(false);

    const isSomeFilterSet = useMemo(() => {
        return (
            fulfilmentFilterSearch.statuses.length > 0 ||
            !!fulfilmentFilterSearch.fulfilmentName ||
            !!fulfilmentFilterSearch.taxPayerWorkspaceId ||
            fulfilmentFilterSearch.withWarnings ||
            !!fulfilmentFilterSearch.expired
        );
    }, [
        fulfilmentFilterSearch.expired,
        fulfilmentFilterSearch.fulfilmentName,
        fulfilmentFilterSearch.statuses.length,
        fulfilmentFilterSearch.taxPayerWorkspaceId,
        fulfilmentFilterSearch.withWarnings,
    ]);

    const handleOnChangeSelectedKPIs = useCallback(
        (selectedKPIs: FulfilmentStatus[]) => {
            fulfilmentSearchFiltersStoreSlice.updateValues({
                withWarnings: !selectedKPIs.includes(FulfilmentStatus.COMPLETED)
                    ? false
                    : fulfilmentFilterSearch.withWarnings,
                statuses: selectedKPIs,
            });
        },
        [fulfilmentFilterSearch.withWarnings],
    );

    const debouncedOnFilterChange = useDebouncedCallback((cb: () => void) => {
        cb();
    }, 500);

    const handleOnChangeFulfilmentName = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const fullTextSearch = event.target.value;
            const regex = new RegExp("^[a-zA-Z0-9\\s._()\\[\\]-]+$");

            if (!!fullTextSearch && !regex.test(fullTextSearch)) {
                const sanitized = fullTextSearch.replace(
                    /[^a-zA-Z0-9\s._()[\]-]/g,
                    "",
                );
                event.target.value = sanitized;
            } else {
                debouncedOnFilterChange(() => {
                    fulfilmentSearchFiltersStoreSlice.updateValues({
                        fulfilmentName: event.target.value,
                    });
                });
            }
        },
        [debouncedOnFilterChange],
    );

    const handleTaxPayerChange = (
        _: SyntheticEvent<Element, Event>,
        value: TaxPayerDto | null,
    ) => {
        fulfilmentSearchFiltersStoreSlice.updateValues({
            taxPayerWorkspaceId: value?.workspaceId || undefined,
        });
    };

    const handleToggleFilters = () => {
        setOpenFilters((previous) => !previous);
    };

    const handleResetFilters = () => {
        fulfilmentSearchFiltersStoreSlice.resetValues();
        if (fullTextSearchInputRef.current) {
            fullTextSearchInputRef.current.value = "";
        }
    };

    const taxPayerOptions = useMemo(() => {
        if (!taxPayers) return [];
        const selected = taxPayers.find(
            (taxPayer) =>
                taxPayer.workspaceId ===
                fulfilmentFilterSearch.taxPayerWorkspaceId,
        );
        return selected
            ? [
                  selected,
                  ...taxPayers.filter(
                      (tp) => tp.workspaceId !== selected.workspaceId,
                  ),
              ]
            : taxPayers;
    }, [fulfilmentFilterSearch.taxPayerWorkspaceId, taxPayers]);

    const selectedTaxPayer = useMemo(
        () =>
            taxPayerOptions.find(
                (taxPayer) =>
                    taxPayer.workspaceId ===
                    fulfilmentFilterSearch.taxPayerWorkspaceId,
            ),
        [fulfilmentFilterSearch.taxPayerWorkspaceId, taxPayerOptions],
    );

    return (
        <Stack spacing={2}>
            {isTaxTinderDialogOpen && (
                <TaxTinderDialog
                    open={isTaxTinderDialogOpen}
                    onClose={() => setIsTaxTinderDialogOpen(false)}
                />
            )}
            <FulfilmentListHeaderKPIs
                accordionRight={
                    <Stack direction="row" spacing={1}>
                        <Can policy="taxTinderApply">
                            <Button
                                onClick={() => {
                                    setIsTaxTinderDialogOpen(true);
                                }}
                                startIcon={
                                    <VaporIcon
                                        color="interactive"
                                        icon={faSparkles}
                                    />
                                }
                                variant="outlined"
                            >
                                <Typography>
                                    {t(
                                        "views.home.components.fulfilment_list_header.tax_tinder.link_fulfilments_btn",
                                    )}
                                </Typography>
                            </Button>
                        </Can>
                        <Can policy="fulfilmentCreate">
                            <Button
                                onClick={onClickAddCustomFulfilment}
                                startIcon={
                                    <VaporIcon color="white" icon={faPlus} />
                                }
                                variant="contained"
                            >
                                <Typography
                                    color={theme.palette.primary.ghostWhite}
                                    variant="bodyLarge500"
                                >
                                    {t(
                                        "views.home.components.fulfilment_list_header.filters.add_custom_fulfilment",
                                    )}
                                </Typography>
                            </Button>
                        </Can>
                    </Stack>
                }
                onChangeSelectedKPIs={handleOnChangeSelectedKPIs}
                statuses={fulfilmentFilterSearch.statuses}
            />
            <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <TextField
                        placeholder={t(
                            "views.home.components.fulfilment_list_header.filters.fulfilment",
                        )}
                        sx={{ width: "20vw" }}
                        inputRef={fullTextSearchInputRef}
                        onChange={handleOnChangeFulfilmentName}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        sx={{
                                            pointerEvents: "none",
                                            background:
                                                "linear-gradient(-26.56505117707799deg, rgb(0, 195, 234) 0%, rgb(0, 144, 209) 100%)",
                                        }}
                                    >
                                        <Search color="white" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Chip
                        label={t(
                            "views.home.components.fulfilment_list_header.filters.expired_fulfilments",
                        )}
                        icon={<VaporIcon icon={faCircle} color="critical" />}
                        onClick={() => {
                            fulfilmentSearchFiltersStoreSlice.updateValues({
                                expired: !fulfilmentFilterSearch.expired,
                            });
                        }}
                        variant={
                            fulfilmentFilterSearch.expired
                                ? "outlined"
                                : "filled"
                        }
                        clickable
                    ></Chip>
                    {(fulfilmentFilterSearch.withWarnings ||
                        (kpiData && kpiData.completedWithWarnings > 0)) &&
                        fulfilmentFilterSearch.statuses.includes(
                            FulfilmentStatus.COMPLETED,
                        ) && (
                            <Chip
                                clickable
                                label={t(
                                    "views.home.components.fulfilment_list_header.filters.fulfilments_with_warnings",
                                    {
                                        warnings:
                                            kpiData?.completedWithWarnings ?? 0,
                                    },
                                )}
                                icon={
                                    <VaporIcon
                                        icon={faWarning}
                                        color="warning"
                                    />
                                }
                                onClick={() => {
                                    fulfilmentSearchFiltersStoreSlice.updateValues(
                                        {
                                            withWarnings:
                                                !fulfilmentFilterSearch.withWarnings,
                                        },
                                    );
                                }}
                                variant={
                                    fulfilmentFilterSearch.withWarnings
                                        ? "outlined"
                                        : "filled"
                                }
                            ></Chip>
                        )}
                    <Button
                        startIcon={
                            <VaporIcon
                                color="interactive"
                                icon={openFilters ? faAngleUp : faAngleDown}
                            />
                        }
                        onClick={handleToggleFilters}
                    >
                        <Typography
                            color={theme.palette.primary.interactiveDefault}
                            variant="bodyLarge500"
                        >
                            {t(
                                "views.home.components.fulfilment_list_header.filters.others_filters",
                            )}
                        </Typography>
                    </Button>
                    <Button
                        disabled={!isSomeFilterSet}
                        onClick={handleResetFilters}
                        size="large"
                        startIcon={
                            <VaporIcon
                                color={isSomeFilterSet ? "primary" : "disabled"}
                                icon={faXmark}
                            />
                        }
                    >
                        <Typography
                            color={
                                isSomeFilterSet
                                    ? theme.palette.primary.interactiveDefault
                                    : theme.palette.primary.textHintColor
                            }
                            variant="bodyLarge500"
                        >
                            {t(
                                "views.home.components.fulfilment_list_header.filters.reset",
                            )}
                        </Typography>
                    </Button>
                </Stack>
                <Tooltip
                    title={t(
                        "views.home.components.fulfilment_list_header.refresh_page",
                    )}
                >
                    <span>
                        <IconButton
                            aria-label={t(
                                "views.home.components.fulfilment_list_header.refresh_page",
                            )}
                            disabled={isRefreshing}
                            onClick={refreshData}
                        >
                            <VaporIcon
                                color={isRefreshing ? "disabled" : "primary"}
                                icon={faRefresh}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
            {openFilters && (
                <Stack direction="row" spacing={2}>
                    <Autocomplete
                        disableClearable={false}
                        disabled={taxPayerOptions.length === 0}
                        filterOptions={(
                            options: TaxPayerDto[],
                            { inputValue },
                        ) => {
                            return options.filter((option) => {
                                const name = option.businessName
                                    ? option.businessName
                                    : `${option.name} ${option.surname}`;
                                return (
                                    name
                                        .toLowerCase()
                                        .includes(inputValue.toLowerCase()) ||
                                    option.taxIdentifier
                                        .toLowerCase()
                                        .includes(inputValue.toLowerCase()) ||
                                    option.vatIdentifier
                                        .toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                );
                            });
                        }}
                        getOptionLabel={(option: TaxPayerDto) =>
                            option.businessName
                                ? option.businessName
                                : `${option.name} ${option.surname}`
                        }
                        isOptionEqualToValue={(
                            option: TaxPayerDto,
                            value: TaxPayerDto,
                        ) => {
                            return option.workspaceId === value.workspaceId;
                        }}
                        noOptionsText={t(
                            "views.home.components.fulfilment_list_header.filters.taxPayer.no_options_text",
                        )}
                        onChange={handleTaxPayerChange}
                        options={taxPayerOptions}
                        renderOption={(props, option) => {
                            return (
                                <ListItem {...props} key={option.workspaceId}>
                                    <Stack>
                                        {option.businessName && (
                                            <Typography>
                                                {option.businessName}
                                            </Typography>
                                        )}
                                        {!option.businessName && (
                                            <Typography>
                                                {option.name} {option.surname}
                                            </Typography>
                                        )}
                                        <Typography
                                            color={
                                                theme.palette.primary
                                                    .textSubduedColor
                                            }
                                        >
                                            {option.taxIdentifier}
                                        </Typography>
                                    </Stack>
                                </ListItem>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t(
                                    "views.home.components.fulfilment_list_header.filters.taxPayer.label",
                                )}
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    shrink: true,
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { width: "20vw" },
                                }}
                                placeholder={t(
                                    "views.home.components.fulfilment_list_header.filters.taxPayer.placeholder",
                                )}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        padding: 0,
                                        paddingLeft: "4px",
                                    },
                                }}
                            />
                        )}
                        style={{ width: "20vw" }}
                        value={selectedTaxPayer || null}
                    />
                </Stack>
            )}
        </Stack>
    );
};
